import { uniqWith } from 'es-toolkit';

import { notNullish, pairwise, sortBy } from '@paragon/utilities';

import { type AxisAlignedBounds, boundsOverlap } from './AxisAlignedBounds';
import Direction2D from './Direction2D';
import HalfPlane2D from './HalfPlane2D';
import Line2D from './Line2D';
import LineSegment3D from './LineSegment3D';
import Point2D, { Point2DInterface } from './Point2D';
import { distanceEquals, isDotProductZero } from './units';
import Vector2D from './Vector2D';

export interface LineSegment2DInterface {
  readonly endPoint: Point2DInterface;
  readonly basePoint: Point2DInterface;
}

class LineSegment2D {
  readonly basePoint: Point2D;
  readonly endPoint: Point2D;

  constructor(basePoint: Point2D, endPoint: Point2D) {
    this.basePoint = basePoint;
    this.endPoint = endPoint;
    if (this.basePoint.equals(this.endPoint)) throw new Error('LineSegment has no breadth!');
  }

  static fromSegmentObject(segment: { readonly start: Point2DInterface; readonly end: Point2DInterface }) {
    return LineSegment2D.fromPointObjects(segment.start, segment.end);
  }

  static fromLineSegmentObject(segment: LineSegment2DInterface) {
    return LineSegment2D.fromPointObjects(segment.basePoint, segment.endPoint);
  }

  static fromPointObjects(basePoint: Point2DInterface, endPoint: Point2DInterface) {
    return new LineSegment2D(Point2D.fromObject(basePoint), Point2D.fromObject(endPoint));
  }

  static fromPointArray(vertices: readonly [Point2DInterface, Point2DInterface]) {
    return LineSegment2D.fromPointObjects(vertices[0], vertices[1]);
  }

  static splitAllSegmentsByEachOther(segments: readonly LineSegment2D[]) {
    return uniqWith(LineSegment2D.splitAllSegmentsByEachOtherWithoutDeduplication(segments), (e1, e2) => e1.equals(e2));
  }

  static splitAllSegmentsByEachOtherWithoutDeduplication(segments: readonly LineSegment2D[]) {
    const allIntersections = uniqWith(
      segments
        .flatMap((edge1) => segments.map((edge2) => edge1.intersectWithSegment(edge2)))
        .filter(notNullish)
        .concat(segments.flatMap(({ endPoints }) => endPoints)),
      (p1, p2) => p1.equals(p2),
    );

    return segments.flatMap((segment) => {
      const candidatePoints = allIntersections.filter((point) => segment.isNotEndPoint(point));
      if (candidatePoints.length === 0) return [segment];

      const line = segment.toLine();
      const interiorPoints = candidatePoints.filter((point) => segment.containsUsingLine(point, line));
      return segment.splitAtUniqueInteriorPoints(interiorPoints, line);
    });
  }

  translate(vector: Vector2D) {
    return new LineSegment2D(this.basePoint.translate(vector), this.endPoint.translate(vector));
  }

  get length() {
    return this.basePoint.distanceTo(this.endPoint);
  }

  private cachedDirection: Direction2D | null = null;
  get direction() {
    if (!this.cachedDirection) this.cachedDirection = Direction2D.fromPoints(this.basePoint, this.endPoint);
    return this.cachedDirection;
  }

  private _axisAlignedBounds: AxisAlignedBounds | null = null;
  get axisAlignedBounds(): AxisAlignedBounds {
    if (!this._axisAlignedBounds) {
      this._axisAlignedBounds = {
        minX: Math.min(this.basePoint.x, this.endPoint.x),
        maxX: Math.max(this.basePoint.x, this.endPoint.x),
        minY: Math.min(this.basePoint.y, this.endPoint.y),
        maxY: Math.max(this.basePoint.y, this.endPoint.y),
      };
    }
    return this._axisAlignedBounds;
  }

  get endPoints(): [Point2D, Point2D] {
    return [this.basePoint, this.endPoint];
  }

  get midpoint() {
    return this.getPointAt(this.length / 2)!;
  }

  toLine() {
    return Line2D.fromLineSegment(this);
  }

  to3D(z = 0) {
    return new LineSegment3D(this.basePoint.to3D(z), this.endPoint.to3D(z));
  }

  reverse() {
    return new LineSegment2D(this.endPoint, this.basePoint);
  }

  intersectWithLine(line: Line2D) {
    const intersection = Line2D.fromLineSegment(this).intersectWithLine(line);
    if (intersection === null) return null;
    if (this.contains(intersection)) return intersection;
    return null;
  }

  intersectWithSegment(segment: LineSegment2D): Point2D | null {
    return !boundsOverlap(this.axisAlignedBounds, segment.axisAlignedBounds) ||
      isDotProductZero(this.direction.crossProductMagnitude(segment.direction))
      ? null
      : this.intersectWithNonParallelSegment(segment);
  }

  /**
   * Assuming the given segment is not parallel to this segment makes this method much faster. This implementation is
   *   for internal use and for performance critical code when skipping that check is appropriate.
   */
  intersectWithNonParallelSegment(segment: LineSegment2D): Point2D | null {
    const intersection = Line2D.intersectLines(this.basePoint, this.endPoint, segment.basePoint, segment.endPoint);
    return this.containsPointKnownToBeOnLine(intersection) && segment.containsPointKnownToBeOnLine(intersection)
      ? intersection
      : null;
  }

  isParallelTo(other: { direction: Direction2D }) {
    return this.direction.isParallelTo(other.direction);
  }

  isPerpendicularTo(other: { direction: Direction2D }) {
    return this.direction.isPerpendicularTo(other.direction);
  }

  contains(point: Point2D) {
    return this.toLine().containsPoint(point) && this.containsPointKnownToBeOnLine(point);
  }

  /**
   * for internal use only, to avoid repeated .toLine() calls and allocations in hot loops
   */
  private containsUsingLine(point: Point2D, line: Line2D): boolean {
    return line.containsPoint(point) && this.containsPointKnownToBeOnLine(point);
  }

  containsSegment(segment: LineSegment2D) {
    return this.contains(segment.basePoint) && this.contains(segment.endPoint);
  }

  /**
   * A faster alternative to `contains` which assumes the given point is known to be on the line containing this
   * segment. Useful for performance-critical code when skipping that check is appropriate.
   */
  containsPointKnownToBeOnLine(point: Point2DInterface) {
    return distanceEquals(this.basePoint.distanceTo(point) + this.endPoint.distanceTo(point), this.length);
  }

  containsOnInside(point: Point2D) {
    return this.isNotEndPoint(point) && this.contains(point);
  }

  equals(segment: LineSegment2D) {
    return (
      (this.basePoint.equals(segment.basePoint) && this.endPoint.equals(segment.endPoint)) ||
      (this.basePoint.equals(segment.endPoint) && this.endPoint.equals(segment.basePoint))
    );
  }

  overlaps(segment: LineSegment2D) {
    return (
      boundsOverlap(this.axisAlignedBounds, segment.axisAlignedBounds) &&
      (this.equals(segment) ||
        (this.isParallelTo(segment) &&
          (this.endPoints.some((endPoint) => segment.containsOnInside(endPoint)) ||
            segment.endPoints.some((endPoint) => this.containsOnInside(endPoint)))))
    );
  }

  overlappingSegment(segment: LineSegment2D): LineSegment2D | null {
    if (!this.overlaps(segment)) return null;

    // Two segments with a tiny angle between them or with a tiny transverse offset between them might still be counted
    // as overlapping. We construct the segment of overlap along the bisector.
    const bisector = Line2D.getSmallestAngleBisector(this.toLine(), segment.toLine());

    const thisSegmentOriented = this.orientWith(bisector.direction);
    const thatSegmentOriented = segment.orientWith(bisector.direction);

    const basePointDistance = Math.max(
      ...[thisSegmentOriented.basePoint, thatSegmentOriented.basePoint].map((point) =>
        point.projectedSignedDistanceAlong(bisector),
      ),
    );
    const endPointDistance = Math.max(
      ...[thisSegmentOriented.endPoint, thatSegmentOriented.endPoint].map((point) =>
        point.projectedSignedDistanceAlong(bisector),
      ),
    );

    const basePoint = bisector.getPointAlongLine(basePointDistance);
    const endPoint = bisector.getPointAlongLine(endPointDistance);

    return basePoint.equals(endPoint) ? null : new LineSegment2D(basePoint, endPoint);
  }

  orientWith(direction: Direction2D) {
    return this.direction.dotProduct(direction) >= 0 ? this : this.reverse();
  }

  getPointAt(distance: number) {
    if (distanceEquals(distance, 0)) return this.basePoint;
    if (distanceEquals(distance, this.length)) return this.endPoint;
    if (distance < 0 || distance > this.length) return null;

    const { direction } = this;
    return new Point2D(this.basePoint.x + direction.x * distance, this.basePoint.y + direction.y * distance);
  }

  sliceBySegment(segment: LineSegment2D): LineSegment2D[] {
    const intersection = this.intersectWithSegment(segment);
    if (intersection) {
      return this.splitAtPoint(intersection);
    }

    return [this];
  }

  splitAtPoint(point: Point2D): LineSegment2D[] {
    if (this.containsOnInside(point)) {
      return [new LineSegment2D(this.basePoint, point), new LineSegment2D(point, this.endPoint)];
    }

    return [this];
  }

  splitAtPoints(points: readonly Point2D[]): LineSegment2D[] {
    return this.splitAtPointsKnownToBeOnSegment(points.filter((point) => this.contains(point)));
  }

  splitAtPointsKnownToBeOnSegment(points: readonly Point2D[]): LineSegment2D[] {
    const uniqueInteriorPoints = uniqWith(
      points.filter((point) => this.isNotEndPoint(point)),
      (a, b) => a.equals(b),
    );
    if (uniqueInteriorPoints.length === 0) return [this];

    return this.splitAtUniqueInteriorPoints(uniqueInteriorPoints, this.toLine());
  }

  private isNotEndPoint(point: Point2DInterface): boolean {
    return !this.basePoint.equals(point) && !this.endPoint.equals(point);
  }

  private splitAtUniqueInteriorPoints(points: readonly Point2D[], line: Line2D): LineSegment2D[] {
    if (points.length === 0) return [this];

    const sortedPoints = sortBy(points, (point) => point.projectedSignedDistanceAlong(line));
    return pairwise([this.basePoint, ...sortedPoints, this.endPoint]).map(
      ([basePoint, endPoint]) => new LineSegment2D(basePoint, endPoint),
    );
  }

  slice(halfPlane: HalfPlane2D): {
    sliceInHalfPlane: LineSegment2D | null;
    sliceOutsideHalfPlane: LineSegment2D | null;
  } {
    const returnVal = (sliceInHalfPlane: LineSegment2D | null, sliceOutsideHalfPlane: LineSegment2D | null) => ({
      sliceInHalfPlane,
      sliceOutsideHalfPlane,
    });

    const basePointSignedDistance = halfPlane.signedDistanceTo(this.basePoint);
    const endPointSignedDistance = halfPlane.signedDistanceTo(this.endPoint);

    if (Math.sign(basePointSignedDistance) * Math.sign(endPointSignedDistance) < 0) {
      const intersection = this.intersectWithLine(halfPlane.boundary);
      if (!intersection) return returnVal(null, null);
      const piece1 = this.basePoint.equals(intersection) ? null : new LineSegment2D(this.basePoint, intersection);
      const piece2 = this.endPoint.equals(intersection) ? null : new LineSegment2D(intersection, this.endPoint);
      return basePointSignedDistance < 0 ? returnVal(piece2, piece1) : returnVal(piece1, piece2);
    }
    if (basePointSignedDistance > 0 || endPointSignedDistance > 0) return returnVal(this, null);
    if (basePointSignedDistance < 0 || endPointSignedDistance < 0) return returnVal(null, this);
    return returnVal(null, null);
  }

  toObject() {
    return {
      basePoint: this.basePoint.toObject(),
      endPoint: this.endPoint.toObject(),
    };
  }

  toSegmentObject() {
    return {
      start: this.basePoint.toObject(),
      end: this.endPoint.toObject(),
    };
  }
}

export default LineSegment2D;
